/* What each route tells a search engine and a link preview.
 *
 * One table, read by the layout above every route, rather than a `useHead`
 * call in twenty six screens. Two reasons, and the second is the important
 * one. A table can be asserted on as data, so `tests/unit/head.test.ts` can
 * check every title and every description in the product against the length
 * rules without a browser. And a route added to `router.tsx` and forgotten
 * here still gets an answer: `headFor` falls through to `noindex, nofollow`
 * and a name, which is the safe direction for a screen that shows a child's
 * work.
 *
 * **Six routes are public and the rest are not.** Public means the screen
 * renders something a stranger can read with no session: the landing, how it
 * works, pricing, the privacy notice, the sources register and the sample
 * report. Everything else is a screen about one family's homework, or a step
 * in getting to one, and it carries `noindex, nofollow` and no canonical.
 *
 * `/resources/:slug` and `/objectives/:slug/practice` are the two that are
 * arguably public: `OakPractice` sends no session header and the service may
 * well answer a stranger. They are left noindex until somebody checks the
 * service rather than the screen, because indexing a route that turns out to
 * need a session publishes a page of 404s.
 *
 * **Nothing here claims anything the product does not do.** No page says
 * every answer is marked, none says a person checks the work, and none says
 * any curriculum body or exam board endorses this. What Oak National Academy
 * material is and how it is licensed is said on `/sources`, in the words that
 * page already uses.
 */
import { OPERATOR_COMPANY } from "../ui/operator";
import { MONTHLY, PACKS, priceOf } from "./plans";
import { SITE, type HeadSpec } from "./head";

/** What the product is, in one sentence, wherever a machine asks. */
const ABOUT =
  "Photograph a page of maths homework and MathsHub explains the answers it "
  + "can check, names the likely mistake behind a wrong one, and points to "
  + "practice on the same topic.";

/* --------------------------------------------------------------- the pages */

/** The six routes a stranger can read, and what each one advertises. */
export const PUBLIC_PAGES: Record<string, { title: string; description: string }> = {
  "/": {
    title: "Maths homework help for UK parents | MathsHub",
    description:
      "Photograph a page of maths homework. See the answers we can check, "
      + "what the mistake was, and practice on the same topic for Years 1 to 11.",
  },
  "/how-it-works": {
    title: "How it works | MathsHub",
    description:
      "How MathsHub reads a photo of finished maths homework, checks the "
      + "answers it can, names the mistake behind a wrong one and finds the "
      + "next practice.",
  },
  "/pricing": {
    title: "Pricing | MathsHub",
    description:
      `One credit marks one page. Packs from ${priceOf(PACKS[0]!)} for `
      + `${PACKS[0]!.pages} pages, or ${priceOf(MONTHLY)} a month for `
      + `${MONTHLY.pages}. Credits never expire and practice is free.`,
  },
  "/privacy": {
    title: "Privacy notice | MathsHub",
    description:
      "What happens to a photo of your child's maths homework: what we read, "
      + "what we keep, who sees it, and how to ask us to delete it.",
  },
  "/sources": {
    title: "Sources and licences | MathsHub",
    description:
      "Where our curriculum material comes from, including Oak National "
      + "Academy lessons under the Open Government Licence, and what each "
      + "licence lets us show.",
  },
  "/sample": {
    title: "Sample report | MathsHub",
    description:
      "A worked example of a MathsHub report on rounding, using invented "
      + "homework. See what comes back before you photograph a page of your "
      + "own.",
  },
};

/**
 * The name a private route puts in the tab, in path order.
 *
 * A pattern rather than a path, because six of these carry an id: a page
 * hash, a job, a paper, a lesson slug. The first match wins, so the more
 * specific pattern is written first.
 */
const PRIVATE_TITLES: [RegExp, string][] = [
  [/^\/signin\/code\/?$/, "Your sign in code"],
  [/^\/signin\/?$/, "Sign in"],
  [/^\/consent\/?$/, "Before we start"],
  [/^\/children\/new\/?$/, "Add a child"],
  [/^\/children\/restore\/?$/, "Restore your children"],
  [/^\/home\/?$/, "Your homework"],
  [/^\/capture\/?$/, "Photograph a page"],
  [/^\/preview\/?$/, "Check the photo"],
  [/^\/type\/?$/, "Type a question"],
  [/^\/reading\//, "Reading the page"],
  [/^\/page\/[^/]+\/confirm\/?$/, "Confirm what was written"],
  [/^\/page\//, "Your report"],
  [/^\/practice\//, "Practice"],
  [/^\/resources\//, "Practice resources"],
  [/^\/objectives\/[^/]+\/practice\/?$/, "Practice resources"],
  [/^\/pages\/?$/, "Your pages"],
  [/^\/paper\//, "A paper"],
  [/^\/account\/?$/, "Your account"],
  [/^\/credits\/?$/, "Credits"],
  [/^\/admin\/?$/, "Admin"],
];

/* ----------------------------------------------------- the structured data */

/** The operator, the site and the product, as three linked things.
 *
 *  `@id` on each, so the three are one graph rather than three unrelated
 *  claims about the same domain.
 *
 *  **The prices are real and the availability says nobody can pay them.**
 *  `plans.ts` holds the operator's numbers and `/pricing` says in its own
 *  words that there is no way to pay yet, so an offer marked as available
 *  would be the one sentence on this site that is not true. `OutOfStock` is
 *  the only value in schema.org's list that means what the pricing page
 *  means. The day a checkout opens, this line changes with it.
 */
export function landingJsonLd(): unknown[] {
  const org = {
    "@type": "Organization",
    "@id": `${SITE}/#organisation`,
    name: OPERATOR_COMPANY,
    url: SITE,
    brand: { "@type": "Brand", name: "MathsHub" },
  };

  const site = {
    "@type": "WebSite",
    "@id": `${SITE}/#website`,
    name: "MathsHub",
    url: SITE,
    inLanguage: "en-GB",
    publisher: { "@id": `${SITE}/#organisation` },
  };

  const app = {
    "@type": "SoftwareApplication",
    "@id": `${SITE}/#app`,
    name: "MathsHub",
    url: SITE,
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    inLanguage: "en-GB",
    description: ABOUT,
    publisher: { "@id": `${SITE}/#organisation` },
    audience: {
      "@type": "EducationalAudience",
      educationalRole: "parent",
      geographicArea: { "@type": "Country", name: "United Kingdom" },
    },
    offers: PACKS.map((pack) => ({
      "@type": "Offer",
      name: `${pack.pages} pages`,
      price: (pack.pence / 100).toFixed(2),
      priceCurrency: pack.currency,
      availability: "https://schema.org/OutOfStock",
      description: pack.note,
    })),
  };

  /* The one question and answer the landing already asks and answers, in the
     landing's own words. There is exactly one, so there is exactly one entry:
     a second would be a question nobody wrote. */
  const faq = {
    "@type": "FAQPage",
    "@id": `${SITE}/#faq`,
    mainEntity: [
      {
        "@type": "Question",
        name: "What if a page cannot be marked?",
        acceptedAnswer: {
          "@type": "Answer",
          text:
            "We always tell you what happened to a page. There are five ways "
            + "it can turn out and three of them cost nothing: only a page we "
            + "mark costs a credit.",
        },
      },
    ],
  };

  return [{ "@context": "https://schema.org", "@graph": [org, site, app, faq] }];
}

/* ------------------------------------------------------------- the lookup */

/** What a public route advertises. Indexable, canonical, and a description. */
function publicSpec(path: string): HeadSpec {
  const page = PUBLIC_PAGES[path]!;
  return {
    title: page.title,
    description: page.description,
    canonical: path,
    robots: "index, follow, max-image-preview:large",
    ...(path === "/" ? { jsonLd: landingJsonLd() } : {}),
  };
}

/**
 * The head for a path.
 *
 * Public routes are matched exactly, because there are six of them and none
 * takes a parameter. Everything else gets a name and `noindex, nofollow`,
 * whether or not this file has heard of it.
 */
export function headFor(pathname: string): HeadSpec {
  /* One trailing slash is not a different page. */
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

  if (Object.prototype.hasOwnProperty.call(PUBLIC_PAGES, path)) return publicSpec(path);

  const named = PRIVATE_TITLES.find(([pattern]) => pattern.test(path));
  return {
    title: `${named ? named[1] : "Page not found"} | MathsHub`,
    robots: "noindex, nofollow",
  };
}
